<template>
  <div>
    <template v-if="order">
      <Header v-if="order">{{ orderDateTime }}</Header>

      <div v-if="order" class="pb-5 mb-5">
        <OrderCard
          isPreview
          :order="order"
          :orderService="orderServiceName"
          :evaluationCount="evaluationCount"
          :evaluationTotalAverage="evaluationScore"
          :workTimeOption="selectedWorkTimeOption"
          displayCarfare
          isCarousel
          isLogin
        />

        <OrderIntroContents :orderDetail="order" class="mt-2">
          <template #unsetOperations>
            <p class="mt-3 text-size-14 text-mild">{{ unset }}</p>
          </template>
        </OrderIntroContents>

        <OrderTabContents
          :order="order"
          :reviewComments="reviewComments"
          :evaluationCount="evaluationCount"
          :evaluationTotalAverage="evaluationScore"
          isPreview
          class="mt-2"
        >
          <template #unsetQualifications>
            <p class="mt-3 text-size-14 text-mild">{{ unset }}</p>
          </template>
          <template #unsetOtherConditionList>
            <p class="mt-3 text-size-14 text-mild">{{ unset }}</p>
          </template>
          <template #unsetClothesConditionList>
            <p class="mt-3 text-size-14 text-mild">{{ unset }}</p>
          </template>
          <template #unsetClothesSampleList>
            <p class="mt-3 text-size-14 text-mild">{{ unset }}</p>
          </template>
          <template #unsetBelongingsList>
            <p class="mt-3 text-size-14 text-mild">{{ unset }}</p>
          </template>
        </OrderTabContents>
        <div v-if="isWatchLaterButtonVisible">
          <OrderFixedButton
            :isShownWatchLaterButton="isWatchLaterButtonVisible"
            :isRegisteredWatchLater="false"
            >この仕事を応募する
          </OrderFixedButton>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="center">
        <div>プレビューに必要な情報が<br />選択されていません。</div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import Header from "@/components/organisms/common/Header.vue";
import OrderCard from "@/components/organisms/orders/OrderCard.vue";
import OrderIntroContents from "@/components/organisms/orders/OrderIntroContents.vue";
import OrderTabContents from "@/components/organisms/orders/OrderTabContents.vue";
import OrderFixedButton from "@/components/organisms/orders/OrderFixedButton.vue";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Component, Vue } from "vue-facing-decorator";
import { Operation } from "@/types/order/Operation";
import { operationsService } from "@/app/services/OperationsService";
import { OrderForPreview } from "@/types/order/OrderForPreview";
import { OrderDetail } from "@/types/order/Order";
import { masterStore } from "@/store/master";
import { orderService } from "@/app/services/OrderService";
import { OrderForPreviewMessage } from "@/types/order/OrderForPreviewMessage";
import { OrderWorkTimeOption } from "@/types/order/OrderWorkTimeOption";
import { formatISO, startOfToday } from "@/utils/date";
import { Qualification } from "@/types/Master";

@Component({
  components: {
    Header,
    OrderCard,
    OrderIntroContents,
    OrderTabContents,
    OrderFixedButton
  }
})
export default class Preview extends Vue {
  // eslint-disable-next-line
  reviewComments: Record<string, any>[] = [];
  unset = "（未設定）";
  qualificationList: Qualification[] = [];
  operationList: Operation[] = [];
  order: OrderDetail | null = null;
  orderServiceName = "";
  evaluationCount = 0;
  evaluationScore = 0;
  selectedWorkTimeOption: OrderWorkTimeOption | null = null;

  mounted() {
    addEventListener("message", this.onMessage);
    // 親のwindowにメッセージを送信する
    parent.postMessage(
      "previewOrderReady",
      process.env.VUE_APP_CORPORATION_URL!
    );
  }

  get orderDateTime() {
    if (!this.order?.date) return "未設定";
    return (
      orderService.getFormattedDateWithSlash(this.order) +
      " " +
      orderService.getFormattedWorkTimeRange(this.order)
    );
  }

  get isWatchLaterButtonVisible() {
    return orderService.isWatchLaterButtonVisible(this.order);
  }
  async onMessage(event: MessageEvent): Promise<void> {
    try {
      this.$loading.show();
      const origin = event.origin;
      // 信頼できるオリジン以外からのメッセージを無視する (重要)
      if (!process.env.VUE_APP_IFRAME_URLS!.split(",").includes(origin)) return;
      const message = event.data;
      const workPreviewMessage: OrderForPreviewMessage = JSON.parse(message);
      if (workPreviewMessage.type !== "orderForPreview") {
        console.warn(`invalid message type: ${workPreviewMessage.type}`);
        return;
      }
      this.setOrderInfo(workPreviewMessage.previewOrder);
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async initMasterDataIfNeeded() {
    // 初期化済みなら何もしない
    if (this.qualificationList.length > 0 && this.operationList.length > 0) {
      return;
    }

    // createdで初期化すると、最初の描画時にqualificationListやoperationListが空のままになるためここで初期化する
    const [qualificationList, { results: operationList }] = await Promise.all([
      masterStore.visibleQualifications,
      operationsService.fetchOperationsList()
    ]);
    this.qualificationList = qualificationList;
    this.operationList = operationList;
  }

  fetchWorkSession({
    hourlyWage,
    variousAllowances,
    startWorkAt,
    finishWorkAt,
    startRestAt,
    finishRestAt
  }: {
    hourlyWage: number | null;
    variousAllowances: number | null;
    startWorkAt: string | null;
    finishWorkAt: string | null;
    startRestAt: string | null;
    finishRestAt: string | null;
  }) {
    if (
      hourlyWage === null ||
      variousAllowances === null ||
      startWorkAt === null ||
      finishWorkAt === null
    ) {
      return {
        legalOvertimeSalary: 0,
        nightworkSalary: 0
      };
    }

    return orderService.fetchWorkSession({
      hourlyWage: hourlyWage,
      variousAllowances: variousAllowances,
      startWorkAt: startWorkAt,
      finishWorkAt: finishWorkAt,
      startRestAt: startRestAt,
      finishRestAt: finishRestAt
    });
  }

  async setOrderInfo(previewOrder: OrderForPreview | null = null) {
    // 一連の情報を初期化
    this.order = null;
    this.evaluationCount = 0;
    this.evaluationScore = 0;
    this.orderServiceName = "";
    this.selectedWorkTimeOption = null;

    if (!previewOrder) {
      return;
    }
    await this.initMasterDataIfNeeded();

    const qualifications = previewOrder?.qualifications;
    const operations = previewOrder?.operations;
    this.evaluationCount = previewOrder.evaluationCount || 0;
    this.evaluationScore = previewOrder.evaluationScore || 0;
    const orderDetail: OrderDetail = {
      office: {
        ...previewOrder.office!
      },
      qualifications:
        qualifications && qualifications.length > 0
          ? this.qualificationList.filter(o => qualifications.includes(o.id))
          : [],
      operations:
        operations && operations.length > 0
          ? this.operationList.filter(o => operations.includes(o.id))
          : [],
      title: previewOrder.title || "",
      date: previewOrder.date || "",
      image1: previewOrder.image1 || "",
      image2: previewOrder.image2 || "",
      image3: previewOrder.image3 || "",
      description: previewOrder.description || "",
      hourlyWage: previewOrder.hourlyWage || 0,
      variousAllowances: previewOrder.variousAllowances || 0,
      otherCondition1: previewOrder.otherCondition1 || "",
      otherCondition2: previewOrder.otherCondition2 || "",
      otherCondition3: previewOrder.otherCondition3 || "",
      otherCondition4: previewOrder.otherCondition4 || "",
      otherCondition5: previewOrder.otherCondition5 || "",
      clothesCondition1: previewOrder.clothesCondition1 || "",
      clothesCondition2: previewOrder.clothesCondition2 || "",
      clothesCondition3: previewOrder.clothesCondition3 || "",
      clothesCondition4: previewOrder.clothesCondition4 || "",
      clothesCondition5: previewOrder.clothesCondition5 || "",
      belongings1: previewOrder.belongings1 || "",
      belongings2: previewOrder.belongings2 || "",
      belongings3: previewOrder.belongings3 || "",
      belongings4: previewOrder.belongings4 || "",
      belongings5: previewOrder.belongings5 || "",
      attachmentFile: previewOrder.attachmentFile,
      workerService: previewOrder.workerService || 1,
      offerType: previewOrder.offerType || 1,
      allowUnqualified: previewOrder.allowUnqualified || false,
      workTimeOptions: await Promise.all(
        previewOrder.workTimeOptions
          .filter(workTimeOption => workTimeOption)
          .map(async (workTimeOption, index) => {
            const workSession = await this.fetchWorkSession({
              hourlyWage: previewOrder.hourlyWage,
              variousAllowances: previewOrder.variousAllowances,
              startWorkAt: workTimeOption.startWorkAt,
              finishWorkAt: workTimeOption.finishWorkAt,
              startRestAt: workTimeOption.startRestAt,
              finishRestAt: workTimeOption.finishRestAt
            });
            return {
              id: index,
              createdAt: "",
              updatedAt: "",
              startWorkAt: workTimeOption.startWorkAt,
              finishWorkAt: workTimeOption.finishWorkAt,
              hasRest: workTimeOption.hasRest,
              startRestAt: workTimeOption.startRestAt,
              finishRestAt: workTimeOption.finishRestAt,
              salaryPerDay: workTimeOption.salaryPerDay || 0,
              additionalPoint: workTimeOption.additionalPoint || 0,
              visibleFrom: workTimeOption.visibleFrom,
              visibleTo: workTimeOption.visibleTo,
              deltaPoint: 0,
              workSession: {
                legalOvertimeSalary: workSession.legalOvertimeSalary,
                nightworkSalary: workSession.nightworkSalary
              }
            };
          })
      ),
      hasSpecificRecommendationPoint: false,
      recommendationPoints: [],
      // プレビュー時はとりあえず当日
      openedAt: formatISO(startOfToday()),
      // プレビューでは使わないプロパティ
      id: 0,
      individualOfferWorker: null,
      service: {
        id: 0,
        createdAt: "",
        updatedAt: "",
        name: "",
        slug: "",
        sortNo: 0,
        nameForStaff: ""
      },
      createdAt: "",
      updatedAt: "",
      version: 1,
      applicantsCount: 0,
      admittedCount: 0,
      staffName: "",
      phoneNumber: "",
      address: "",
      access: "",
      latitude: "",
      longitude: "",
      allowInexperienced: false,
      status: "drafted",
      clothesSampleImage1: "",
      clothesSampleImage2: "",
      clothesSampleImage3: "",
      firedReason: "",
      template: null,
      workType: "",
      isHiring: true,
      isLimited: false,
      isCorporationLimited: false,
      isUrlLimited: false,
      isAutoOpened: false,
      autoOpenOffset: 0,
      isPointIncreased: false,
      isAlreadyCanceledByMyself: false,
      isIndividualOffer: false
    };

    const service = masterStore.services.find(
      service => service.id === previewOrder.office?.serviceTypeNumber
    );
    const orderServiceName = service?.name;
    this.order = orderDetail;
    this.orderServiceName = orderServiceName || "";
    this.selectedWorkTimeOption = orderDetail.workTimeOptions[0];
  }
}
</script>

<style lang="scss" scoped>
.center {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
}
</style>
